import DOMPurify from 'dompurify'

/**
 * Sanitize HTML while allowing Anear web components (`anear-*` custom elements).
 *
 * DOMPurify strips custom elements by default. We:
 * - Detect which `anear-*` tags are present in the HTML and add them to `ADD_TAGS`
 * - Allow the attributes used by Anear web components via `ADD_ATTR`
 */
export const sanitizeAnearHtml = (html) => {
  // Extract any anear-* custom elements from the HTML to add them to the allowlist
  const anearTagPattern = /<(\/?)(anear-[a-z-]+)/gi
  const foundTags = new Set()
  let match
  while ((match = anearTagPattern.exec(html)) !== null) {
    foundTags.add(match[2].toLowerCase())
  }

  // Always allow anear-anchor so public markup with injection targets is not stripped
  const baseAnearTags = ['anear-anchor']
  const config = {
    // Add base anear tags and any found anear-* tags from the HTML
    ADD_TAGS: [...baseAnearTags, ...Array.from(foundTags)],
    // Allow attributes used by Anear web components
    ADD_ATTR: [
      'id',
      'expand',
      'action',
      // child app view/config flags
      'compact',
      // allow designers to control whether ABR prompts on exit/back
      'prompt',
      'stay',
      'no_prompt',
      'no-prompt',
      'payload',
      // action timeouts (ABR-local)
      'duration',
      'started-at',
      'remaining-ms',
      'timeout-id',
      'reset',
      'reset-action',
      'exit',
      'exit-action',
      'behavior',
      'form',
      'name',
      'value',
      'position-value',
      'media-value',
      // anear-framed-image opaque media / legacy framing
      'media',
      'position',
      'alt',
      'min',
      'max',
      'step',
      'options',
      'items',
      'accept',
      'purpose',
      'label',
      'title',
      'data-anear-help-layout',
      'data-anear-help-close',
      'role',
      'icon-style',
      'style',
      'placeholder',
      'preview-target',
      'preview-alt',
      'filename-field',
      'filename-value',
      'filename-target',
      'position-field',
      'submit-mode',
      'submit-on-success',
      'submit-on-change',
      'submit-on-empty',
      'submit-name',
      'submit-value',
      // anear-file-upload image optimization (ABR)
      'optimize-images',
      'max-source-mb',
      'max-edge-px',
      'passthrough-max-kb',
      'webp-quality',
      // anear-image-upload editor configuration
      'crop-mode',
      'adjust-label',
      'editor-title',
      'crop-aspect',
      // source pixel dimensions of the uploaded photo (anear-image-upload)
      'width-field',
      'height-field',
      'sync-clock',
      'poster-purpose',
      'max-seconds',
      'min-seconds',
      'max-bitrate',
      'facing-mode',
      'uploading-label',
      'replace-label',
      'max-upload-mb',
      'max-upload-seconds',
      'auto-transcode',
      'transcode-workflow',
      'stay-open',
      'chrome',
      'camera-style',
      'modes',
      'default-mode',
      'purpose-photo',
      'purpose-video',
      'invalid-type-message',
      // anear-video playback
      'src',
      'hls',
      'mp4',
      'status',
      'poster',
      'controls',
      'muted',
      'autoplay',
      'loop',
      'artist',
      // anear-workflow (generic ANAPI workflow trigger). A single JSON `input`
      // attribute carries arbitrary per-workflow input, so no future workflow
      // needs a sanitizer change. Server-side contracts allowlist input keys.
      'workflow',
      'input',
      'auto',
      'poll',
      'poll-interval',
      'run-id',
      'show-status',
    ],
  }

  return DOMPurify.sanitize(html, config)
}

